/**
 * Family Page - Guardian View
 *
 * Allows guardians to view and manage their linked athletes.
 * Shows athlete cards with quick access to view their videos.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listFamilyLinks,
  updateNotificationPreferences,
  type Organization,
  type FamilyLink,
} from '@tss/shared';
import {
  Video,
  UserPlus,
  Bell,
  BellOff,
  Mail,
  CheckCircle,
  Clock,
  UserCheck,
  ArrowRight,
} from 'lucide-react';
import { PageHeader, Skeleton } from '../components/UI';
import { Button, EmptyState } from '@tss/shared/ui';

interface ProxyAthlete {
  athlete_id: string;
  athlete_name: string;
}

interface FamilyPageProps {
  org: Organization | null;
  onSelectAthlete?: (athlete: ProxyAthlete) => void;
  proxyAthlete?: ProxyAthlete | null;
  onExitProxyMode?: () => void;
}

export function FamilyPage({
  org,
  onSelectAthlete,
  proxyAthlete,
  onExitProxyMode,
}: FamilyPageProps) {
  const [links, setLinks] = useState<FamilyLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (org) {
      loadLinks();
    } else {
      setLoading(false);
    }
  }, [org]);

  async function loadLinks() {
    if (!org) return;
    setLoading(true);
    try {
      const { links: linkList } = await listFamilyLinks(org.org_id);
      setLinks(linkList || []);
    } catch (err) {
      console.error('Failed to load family links:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleNotifications(link: FamilyLink) {
    if (!org) return;
    try {
      // Toggle all notification preferences together
      const newValue = !link.notify_weekly_progress;
      await updateNotificationPreferences(org.org_id, link.link_id, {
        notify_weekly_progress: newValue,
        notify_monthly_progress: newValue,
        notify_flags: newValue,
      });
      await loadLinks();
    } catch (err) {
      console.error('Failed to update notification settings:', err);
    }
  }

  const approvedLinks = links.filter((l) => l.status === 'APPROVED');
  const pendingLinks = links.filter((l) => l.status === 'PENDING');
  const managedAthletes = approvedLinks.filter((l) => l.is_guardian_managed);

  if (loading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Skeleton height={36} width="40%" style={{ marginBottom: 8 }} />
        <Skeleton height={20} width="55%" style={{ marginBottom: 32 }} />
        <Skeleton.Card count={3} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <PageHeader
        title="My Athletes"
        description="View and manage your linked athletes"
        icon={UserCheck}
        iconColor="var(--purple-600)"
      />

      {/* Managed Athletes Switcher - prominently shown at top */}
      {managedAthletes.length > 0 && (
        <div
          style={{
            marginBottom: '32px',
            padding: '20px',
            background:
              'linear-gradient(135deg, rgba(var(--purple-rgb), 0.08) 0%, rgba(168, 85, 247, 0.05) 100%)',
            border: '1px solid rgba(var(--purple-rgb), 0.2)',
            borderRadius: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <UserCheck size={18} style={{ color: 'var(--purple-600)' }} />
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: 'var(--gray-900)' }}>
              Managed Athletes
            </h3>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginBottom: '16px' }}>
            Act on behalf of your minor child to view programs, upload videos, and track progress.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {managedAthletes.map((link) => {
              const isActive = proxyAthlete?.athlete_id === link.athlete_id;
              return (
                <button
                  key={link.link_id}
                  onClick={() => {
                    if (isActive && onExitProxyMode) {
                      onExitProxyMode();
                    } else if (onSelectAthlete) {
                      onSelectAthlete({
                        athlete_id: link.athlete_id,
                        athlete_name: link.athlete_name,
                      });
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    background: isActive
                      ? 'linear-gradient(135deg, var(--color-success) 0%, #16a34a 100%)'
                      : 'white',
                    border: isActive ? 'none' : '1px solid var(--gray-200)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: isActive
                      ? '0 4px 12px rgba(var(--green-rgb), 0.25)'
                      : '0 2px 4px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: isActive
                        ? 'rgba(255, 255, 255, 0.2)'
                        : 'linear-gradient(135deg, var(--purple-600) 0%, #a855f7 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '600',
                    }}
                  >
                    {link.athlete_name?.charAt(0)?.toUpperCase() || 'A'}
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <p
                      style={{
                        fontWeight: '600',
                        color: isActive ? 'white' : 'var(--gray-900)',
                        fontSize: '14px',
                      }}
                    >
                      {link.athlete_name}
                    </p>
                    <p
                      style={{
                        fontSize: '12px',
                        color: isActive ? 'rgba(255,255,255,0.8)' : 'var(--gray-500)',
                      }}
                    >
                      {isActive ? 'Currently active' : 'Click to act as'}
                    </p>
                  </div>
                  {!isActive && (
                    <ArrowRight size={16} style={{ color: 'var(--gray-400)', marginLeft: '4px' }} />
                  )}
                  {isActive && (
                    <UserCheck size={16} style={{ color: 'white', marginLeft: '4px' }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {links.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<UserPlus />}
            title="No athletes linked yet"
            description="Your athlete's coach will send you an invite to connect. Check your email for the invite link."
          />
        </div>
      ) : (
        <>
          {/* Pending Links */}
          {pendingLinks.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h2
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--gray-700)',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Clock size={18} style={{ color: '#f59e0b' }} />
                Pending Approval
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
                  gap: '16px',
                }}
              >
                {pendingLinks.map((link) => (
                  <PendingLinkCard key={link.link_id} link={link} />
                ))}
              </div>
            </div>
          )}

          {/* Approved Links */}
          {approvedLinks.length > 0 && (
            <div>
              <h2
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--gray-700)',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <CheckCircle size={18} style={{ color: 'var(--color-success)' }} />
                Connected Athletes ({approvedLinks.length})
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
                  gap: '16px',
                }}
              >
                {approvedLinks.map((link) => (
                  <AthleteCard
                    key={link.link_id}
                    link={link}
                    onToggleNotifications={() => handleToggleNotifications(link)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Info section */}
      <div
        style={{
          marginTop: '32px',
          padding: '20px',
          background: 'var(--gray-50)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--gray-200)',
        }}
      >
        <h3
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--gray-700)',
            marginBottom: '8px',
          }}
        >
          How Family Links Work
        </h3>
        <ul
          style={{
            fontSize: '13px',
            color: 'var(--gray-600)',
            paddingLeft: '20px',
            margin: 0,
          }}
        >
          <li style={{ marginBottom: '6px' }}>
            Coaches send family invites to connect you with your athlete
          </li>
          <li style={{ marginBottom: '6px' }}>
            Once connected, you can view your athlete's training videos
          </li>
          <li>Enable notifications to receive email alerts when new videos are uploaded</li>
        </ul>
      </div>
    </div>
  );
}

function AthleteCard({
  link,
  onToggleNotifications,
}: {
  link: FamilyLink;
  onToggleNotifications: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="card" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--purple-600) 0%, #a855f7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '20px',
            fontWeight: '600',
            flexShrink: 0,
          }}
        >
          {link.athlete_name?.charAt(0)?.toUpperCase() || 'A'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            className="truncate"
            style={{
              fontWeight: '600',
              color: 'var(--gray-900)',
              fontSize: '16px',
              marginBottom: '4px',
            }}
          >
            {link.athlete_name || 'Athlete'}
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--gray-500)', textTransform: 'capitalize' }}>
            {link.relationship.toLowerCase()}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          onClick={() => navigate('/videos')}
          iconLeft={<Video size={16} />}
          style={{ flex: 1 }}
        >
          View Videos
        </Button>
        <Button
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            onToggleNotifications();
          }}
          title={link.notify_weekly_progress ? 'Disable notifications' : 'Enable notifications'}
        >
          {link.notify_weekly_progress ? (
            <Bell size={16} style={{ color: 'var(--purple-600)' }} />
          ) : (
            <BellOff size={16} style={{ color: 'var(--gray-400)' }} />
          )}
        </Button>
      </div>
    </div>
  );
}

function PendingLinkCard({ link }: { link: FamilyLink }) {
  return (
    <div
      className="card"
      style={{
        padding: '16px 20px',
        background: 'rgba(245, 158, 11, 0.05)',
        border: '1px solid rgba(245, 158, 11, 0.2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div
          className="icon-container icon-container-md"
          style={{ background: 'rgba(245, 158, 11, 0.15)' }}
        >
          <Mail size={20} style={{ color: '#f59e0b' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            className="truncate"
            style={{
              fontWeight: '600',
              color: 'var(--gray-900)',
              fontSize: '15px',
              marginBottom: '4px',
            }}
          >
            {link.athlete_name || 'Athlete'}
          </h3>
          <p style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '500' }}>
            Awaiting athlete approval
          </p>
        </div>
      </div>
    </div>
  );
}
